import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Activity, Filter } from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_COLORS = {
  CREATE: 'bg-green-100 text-green-700',
  UPDATE: 'bg-blue-100 text-blue-700',
  DELETE: 'bg-red-100 text-red-700',
  LOGIN: 'bg-purple-100 text-purple-700',
  APPROVE: 'bg-teal-100 text-teal-700',
  RELEASE: 'bg-orange-100 text-orange-700',
};

const MODULE_LABELS = {
  tasks: 'Tasks', maintenance: 'Maintenance', assets: 'Assets', auth: 'Auth',
  production: 'Production', inventory: 'Inventory', procurement: 'Procurement',
  users: 'Users', settings: 'Settings', scrap: 'Scrap', tyres: 'Tyres',
};

const ActivityLog = ({ user }) => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ module: '', action: '', user_id: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [users, setUsers] = useState([]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (filters.module) params.module = filters.module;
      if (filters.action) params.action = filters.action;
      if (filters.user_id) params.user_id = filters.user_id;

      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/activity-logs`, { headers: { Authorization: `Bearer ${token}` }, params }),
        axios.get(`${API}/admin/activity-logs/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setLogs(logsRes.data || []);
      setStats(statsRes.data);
    } catch {
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data || []);
    } catch {
      // not critical
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchUsers();
  }, [fetchLogs]);

  const handleFilterChange = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));
  const clearFilters = () => setFilters({ module: '', action: '', user_id: '' });

  const formatDate = (iso) => {
    try { return format(new Date(iso), 'dd MMM yyyy, HH:mm'); } catch { return iso; }
  };

  const allModules = [...new Set(logs.map(l => l.module))];
  const allActions = [...new Set(logs.map(l => l.action))];

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="activity-log-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
            <Activity className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">User Activity Log</h1>
            <p className="text-sm text-slate-500">Track key actions performed across all modules</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 shadow-sm"
            data-testid="toggle-filters"
          >
            <Filter className="w-4 h-4" />
            Filters
            {(filters.module || filters.action || filters.user_id) && (
              <span className="w-2 h-2 bg-indigo-500 rounded-full" />
            )}
          </button>
          <button
            onClick={fetchLogs}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 shadow-sm"
            data-testid="refresh-logs"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center shadow-sm" data-testid="stat-total-events">
            <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            <p className="text-xs text-slate-500 mt-1">Total Events</p>
          </div>
          {Object.entries(stats.by_action || {}).slice(0, 3).map(([action, count]) => (
            <div key={action} className="bg-white rounded-xl border border-slate-200 p-4 text-center shadow-sm" data-testid={`stat-action-${action.toLowerCase()}`}>
              <p className="text-2xl font-bold text-slate-900">{count}</p>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full mt-1 inline-block ${ACTION_COLORS[action] || 'bg-slate-100 text-slate-600'}`}>{action}</span>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 shadow-sm" data-testid="filters-panel">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Module</label>
              <select
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={filters.module}
                onChange={e => handleFilterChange('module', e.target.value)}
                data-testid="filter-module"
              >
                <option value="">All Modules</option>
                {allModules.map(m => <option key={m} value={m}>{MODULE_LABELS[m] || m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Action</label>
              <select
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={filters.action}
                onChange={e => handleFilterChange('action', e.target.value)}
                data-testid="filter-action"
              >
                <option value="">All Actions</option>
                {allActions.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">User</label>
              <select
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={filters.user_id}
                onChange={e => handleFilterChange('user_id', e.target.value)}
                data-testid="filter-user"
              >
                <option value="">All Users</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg bg-white"
              data-testid="clear-filters"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Log Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="activity-log-table">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No activity logs found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Action</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Module</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Description</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">User</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50 transition-colors" data-testid={`log-row-${log.id}`}>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-600'}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                        {MODULE_LABELS[log.module] || log.module}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700 max-w-xs truncate" title={log.description}>
                      {log.description}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-700 text-xs">{log.user_name}</p>
                        <p className="text-slate-400 text-xs">{log.user_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                      {formatDate(log.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-slate-400 text-center mt-4">
        Showing {logs.length} most recent events
      </p>
    </div>
  );
};

export default ActivityLog;
